import {CommonModule} from '@angular/common';
import {Component, DestroyRef, OnInit, ViewChild, inject, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeMenuComponent, SeFeMenuModule, SeFeMenuOptions} from 'se-fe-menu';
import {SeFePaginationOptions} from 'se-fe-pagination';
import {ProductTableComponent} from '../../common/components/product-table/product-table.component';
import {ProductQueryParams} from '../../models';
import {Bundle} from '../../models/clare';
import {selectSelectedOrganizationId} from '../../state/router/router.selectors';
import {ListPageScrollService} from '../../utils/pagination-utils';
import {
  buildProductListQueryParams,
  buildProductListUrlQueryParams,
  mergeProductQueryParams,
  missingDefaultProductQueryParams,
} from '../product-list/store/product-list-query-params';
import {
  selectProductActive,
  selectProductCatalogId,
  selectProductQuery,
  selectProductSortBy,
  selectProductSortDirection,
} from '../product-list/store/product-list.selectors';
import {BundleListActions} from './store/bundle-list.actions';
import {selectBundleListLoading, selectBundleListPaginationOptions, selectBundles} from './store/bundle-list.selectors';

/**
 * Only displays product bundles, not individual products.
 *
 * @see ProductListComponent for individual products
 */
@Component({
  selector: 'bundle-list',
  templateUrl: 'bundle-list.component.html',
  imports: [
    CommonModule,
    ProductTableComponent,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeGridModule,
    SeFeIconModule,
    SeFeMenuModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../commerce-list.scss',
})
export class BundleListComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @ViewChild('mobileMenu') public menu: SeFeMenuComponent;

  bundles$: Observable<Bundle[]>;
  bundlesLoading$: Observable<boolean>;
  paginationOptions$: Observable<SeFePaginationOptions>;
  queryString$: Observable<string>;
  active$: Observable<boolean | undefined>;
  catalogId$: Observable<number | undefined>;
  sortBy$: Observable<string | undefined>;
  sortDirection$: Observable<ProductQueryParams['direction']>;
  queryParams: ProductQueryParams = buildProductListQueryParams({});

  mobileMenuOptions: SeFeMenuOptions = {name: 'mobileMenu'};

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private listPageScroll: ListPageScrollService
  ) {
    this.bundles$ = this.store.select(selectBundles);
    this.bundlesLoading$ = this.store.select(selectBundleListLoading);
    this.paginationOptions$ = this.store.select(selectBundleListPaginationOptions);
    this.queryString$ = this.store.select(selectProductQuery);
    this.active$ = this.store.select(selectProductActive);
    this.catalogId$ = this.store.select(selectProductCatalogId);
    this.sortBy$ = this.store.select(selectProductSortBy);
    this.sortDirection$ = this.store.select(selectProductSortDirection);
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const queryParams = buildProductListQueryParams(params);

      if (missingDefaultProductQueryParams(params)) {
        this.navigateToQueryParams(queryParams, true);
        return;
      }

      this.queryParams = queryParams;
      this.store.dispatch(BundleListActions.loadBundles({queryParams}));
    });
  }

  queryChanged(queryParams: ProductQueryParams): void {
    const mergedParams = mergeProductQueryParams(queryParams, this.queryParams);
    this.navigateToQueryParams(mergedParams);
    this.listPageScroll.scrollToTop();
  }

  toggleActiveStatus({productId, active}: {productId: string; active: boolean}): void {
    this.store.dispatch(BundleListActions.toggleBundleActiveStatus({productId, active}));
  }

  createProductBundle(): void {
    this.store
      .select(selectSelectedOrganizationId)
      .pipe(take(1))
      .subscribe(organizationId => {
        if (organizationId) {
          this.router.navigate(['/org', organizationId, 'commerce', 'bundles', 'new']);
        }
      });
  }

  private navigateToQueryParams(queryParams: ProductQueryParams, replaceUrl = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildProductListUrlQueryParams(queryParams),
      replaceUrl,
    });
  }
}
